import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseGrandTotalsCsv } from '../utils/csvParser';
import { GrandTotalRow } from '../types';
import { SHOW_GRAND_RESULTS } from '../config/featureFlags';

const MEDALS = ['🥇', '🥈', '🥉'];

const Home: React.FC = () => {
  const [rows, setRows] = useState<GrandTotalRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!SHOW_GRAND_RESULTS) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setRows(null);

    const url = `${process.env.PUBLIC_URL}/data/season-grand-totals-summary.csv`;
    fetch(url, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load results (HTTP ${res.status}) from ${url}`);
        }
        return res.text();
      })
      .then((text) => setRows(parseGrandTotalsCsv(text)))
      .catch((err) => {
        const message = err && err.message ? err.message : 'Failed to load results';
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [retryCount]);

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((row) => row.name.toLowerCase().includes(q));
  }, [rows, search]);

  const header = (
    <div style={{ textAlign: 'center', margin: '32px 0' }}>
      <h1>Bible@500 Season 4</h1>
    </div>
  );

  const booksButton = (
    <div style={{ textAlign: 'center', margin: '24px 0' }}>
      <Link to="/books" className="button button-gold">📖 View Individual Book Results</Link>
    </div>
  );

  if (!SHOW_GRAND_RESULTS) {
    return (
      <div className="container">
        {header}
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <h2>Season Scoreboard</h2>
          <p style={{ color: '#6c757d' }}>Season results aren't available yet. Check back soon.</p>
        </div>
        {booksButton}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        {header}
        <div className="loading">Loading season results...</div>
        {booksButton}
      </div>
    );
  }

  if (error || !rows) {
    return (
      <div className="container">
        {header}
        <div className="error">{error || 'Could not load season results.'}</div>
        <button
          className="button"
          style={{ marginTop: '16px' }}
          onClick={() => setRetryCount((n) => n + 1)}
        >
          Retry
        </button>
        {booksButton}
      </div>
    );
  }

  // One card per position (1st/2nd/3rd), tied participants share a card —
  // same dense-rank grouping pattern used on the per-book results pages.
  const podiumGroups = [1, 2, 3]
    .map((rank) => {
      const tied = rows.filter((row) => row.rank === rank);
      return tied.length > 0
        ? { rank, score: tied[0].grandTotal, names: tied.map((row) => row.name || 'Unnamed participant') }
        : null;
    })
    .filter((group): group is { rank: number; score: number; names: string[] } => group !== null);

  return (
    <div className="container">
      {header}

      <div className="champions-showcase">
        <div className="champions-showcase-header">
          <span className="champions-showcase-trophy">🏆</span>
          <h2>Grand Champions</h2>
          <p className="champions-showcase-tagline">Season 4 top scorers across every book</p>
        </div>
        {podiumGroups.length === 0 ? (
          <p style={{ textAlign: 'center' }}>No results yet.</p>
        ) : (
          <div className="champions-podium">
            {podiumGroups.map((group) => (
              <div key={group.rank} className={`champion-tier champion-tier-${group.rank}`}>
                <div className="champion-tier-medal">{MEDALS[group.rank - 1]}</div>
                <div className={`champion-tier-names ${group.names.length > 1 ? 'champion-tier-names-tied' : ''}`}>
                  {group.names.map((name, i) => (
                    <div key={`${group.rank}-${i}`} className="champion-tier-name" title={name}>
                      {name}
                    </div>
                  ))}
                </div>
                <div className="champion-tier-score">{group.score}</div>
                <div className="champion-tier-label">
                  {group.rank === 1 ? '1st Place' : group.rank === 2 ? '2nd Place' : '3rd Place'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {booksButton}

      <div className="container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <h2 style={{ margin: 0 }}>Season Scoreboard</h2>
          <input
            type="text"
            className="search-box"
            style={{ margin: 0 }}
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Name</th>
                <th>Email</th>
                <th>Books Attempted</th>
                <th>Grand Total</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => (
                <tr key={`${row.email}-${idx}`} className={row.rank <= 3 ? `top-rank rank-${row.rank}` : ''}>
                  <td>
                    <strong>{row.rank <= 3 ? MEDALS[row.rank - 1] : row.rank}</strong>
                  </td>
                  <td title={row.name}>{row.name || 'Unnamed participant'}</td>
                  <td title={row.maskedEmail}>{row.maskedEmail}</td>
                  <td>{row.booksAttempted}</td>
                  <td>
                    <strong>{row.grandTotal}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredRows.length === 0 && (
          <p style={{ marginTop: '16px' }}>No participants match "{search}".</p>
        )}
      </div>
    </div>
  );
};

export default Home;
